import {useTranslation} from "react-i18next";
import styles from './ActivityTypeEditComponent.module.scss'
import FlintLoadingWrapper from "@components/page_components/common/loading/FlintLoadingWrapper";
import {ApiResponseDto, BasePopupProps} from "@utils/Models";
import {useEffect, useState} from "react";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import FlintButton from "@components/controls/FlintButton";
import FlintDropDownList from "@components/controls/FlintDropDownList";
import FlintTextBox from "@components/controls/FlintTextBox";
import axios from "axios";
import {ApiUrl_WorkEvaluation_OperationTypeEdit} from "@utils/Endpoints";
import {OperationTypeEditModel, OperationTypeModel} from "@pages/work_evaluation/workevaluation.models";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import * as Yup from "yup"
import useVivoValidation from "@utils/validation/useVivoValidation";
import {bool} from "yup";

export default function ActivityTypeEditComponent(props: ActivityTypeEditComponentProps) {
    const {t} = useTranslation();
    const {state, utils, handlers} = useActivityTypeEditComponent(props);

    return (
        <FlintLoadingWrapper showLoading={state.isLoading.get}>
            <div className={`dialog-content-wrapper`}>
                <div className={`${styles.panelWrapper}`}>
                    <div className={`form-group`}>
                        <FlintDropDownList
                            required={true}
                            label={t("COMMON_CATEGORY")}
                            usePrimitives={true}
                            selectedValue={state.operationType.get?.CatId}
                            onItemSelected={x => state.operationType.set("CatId", x.Id)}
                            validationMessage={state.validationErrors.get.CatId}
                            valid={state.validationErrors.get.CatId == null}
                            dataSource={state.categoryList.get}/>
                    </div>
                    <div className={`form-group`}>
                        <FlintDropDownList
                            required={true}
                            label={t("COMMON_ACTIVITY_TYPE")}
                            usePrimitives={true}
                            selectedValue={state.operationType.get?.TimeMeasurementActivityId}
                            onItemSelected={x => state.operationType.set("TimeMeasurementActivityId", x.Id)}
                            validationMessage={state.validationErrors.get.TimeMeasurementActivityId}
                            valid={state.validationErrors.get.TimeMeasurementActivityId == null}
                            dataSource={state.measurementActivityTypes.get}/>
                    </div>
                    <div className={`form-group`}>
                        <FlintTextBox
                            label={t("lblOperationType")}
                            value={state.operationType.get?.Name}
                            onChange={x => state.operationType.set("Name", x.value)}
                            validationMessage={state.validationErrors.get.Name}
                            valid={state.validationErrors.get.Name == null}
                            required={true}
                            maxLength={50}/>
                    </div>
                </div>
                <div className={"dialog-actions"}>
                    <WindowActionsBar layout={"stretched"}>
                        <FlintButton themeColor={"secondary"}
                                     onClick={handlers.handleCancelled}>{t("Cancel")}</FlintButton>
                        <FlintButton themeColor={"primary"}
                                     onClick={handlers.handleSaveClicked}>{t("Save")}</FlintButton>
                    </WindowActionsBar>
                </div>
            </div>
        </FlintLoadingWrapper>
    );
}

export interface ActivityTypeEditComponentProps extends BasePopupProps<OperationTypeModel> {
    activityTypeId: number;
    isReadOnly?: boolean;
}

function useActivityTypeEditComponent(props: ActivityTypeEditComponentProps) {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const {isReadOnly = false, ...other} = props;
    const {showNotification} = useFlintNotificationContext();
    const [model, setModel] = useState<OperationTypeEditModel>();
    const [errors, setErrors] = useState<any>({});

    const scheme = Yup.object({
        CatId: Yup.number().min(1, "Category is required").required("Category is required"),
        TimeMeasurementActivityId: Yup.number().notOneOf([0], "Activity type is required").required("Activity type is required"),
        Name: Yup.string().required("Operation type is required"),
    })

    function validateForm() {
        const errors: any = {};
        return scheme.validate(model?.OperationType, {abortEarly: false})
            .then((result) => {
                return Promise.resolve(true);
            })
            .catch(error => {
                error.inner.forEach((e: any) => {
                    errors[e.path] = e.message;
                })
                return Promise.resolve(false);
            })
            .finally(() => {
                setErrors(errors);
            });

    }

    function handleSaveClicked() {
        return validateForm().then((isValid) => {
            if (!isValid)
                return;
            setIsLoading(true);
            axios.post<ApiResponseDto<OperationTypeModel>>(ApiUrl_WorkEvaluation_OperationTypeEdit, model?.OperationType)
                .then((response) => {
                    props.OnConfirmedAndSaved(response.data.Result);
                })
                .catch((error) => showNotification(error))
                .finally(() => setIsLoading(false));

        })
    }

    useEffect(() => {
        loadData().then()
    }, [])

    function loadData() {
        setIsLoading(true);
        return axios.get<ApiResponseDto<OperationTypeEditModel>>(ApiUrl_WorkEvaluation_OperationTypeEdit, {params: {id: props.activityTypeId}})
            .then(response => {
                setModel(response.data.Result);
            })
            .catch(error => showNotification(error))
            .finally(() => setIsLoading(false))
    }

    function updateModel<T extends keyof OperationTypeModel>(key: T, value: OperationTypeModel[T]) {
        setModel(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                OperationType: {
                    ...prev.OperationType,
                    [key]: value
                }
            };
        });
    }

    return {
        state: {
            isLoading: {get: isLoading},
            categoryList: {get: model?.OperationCategoryList ?? []},
            measurementActivityTypes: {get: model?.MeasurementActivityTypes ?? []},
            operationType: {get: model?.OperationType, set: updateModel},
            validationErrors: {get: errors}
        },
        utils: {
            isReadOnly: isReadOnly,
        },
        handlers: {
            handleSaveClicked,
            handleCancelled: props.OnCancelled,
        },
    };
}
